import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getImageUrl } from '../utils/api';

const PageContainer = styled.div`
  min-height: 70vh;
  background: #f8f9fa;
  padding: 60px 20px;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  text-align: center;
  margin-bottom: 16px;
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 1rem;
  color: #666;
  margin-bottom: 40px;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 540px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled.button`
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
  display: block;
  background: #fff;
`;

const CardTitle = styled.div`
  padding: 8px 10px;
  font-size: 0.8rem;
  color: #555;
  background: #fff;
  text-align: center;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 10px;
  max-width: 900px;
  width: 100%;
  padding: 16px;
  position: relative;
`;

const ModalImageWrapper = styled.div`
  max-height: 80vh;
  overflow: auto;
`;

const ModalImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ModalTitle = styled.h2`
  font-size: 1.2rem;
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 1.4rem;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  color: #666;

  &:hover {
    color: #000;
  }
`;

const EmptyText = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #666;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const Certificates = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await fetch('/api/certificates');
        if (!response.ok) {
          throw new Error('Ошибка загрузки сертификатов');
        }
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setItems(data.data);
        } else {
          setItems([]);
        }
      } catch (e) {
        console.error('Ошибка загрузки сертификатов:', e);
        setError(e.message || 'Ошибка загрузки сертификатов');
      } finally {
        setLoading(false);
      }
    };

    fetchCertificates();
  }, []);

  const handleOpen = (item) => {
    setSelected(item);
  };

  const handleClose = () => {
    setSelected(null);
  };

  return (
    <PageContainer>
      <Content>
        <Title>Сертификаты качества</Title>
        <Subtitle>
          Все продукты Lagom Life проходят обязательную сертификацию в соответствии с требованиями
          законодательства РФ. Ниже представлены действующие сертификаты и декларации соответствия.
        </Subtitle>

        {loading && (
          <EmptyText>Загрузка сертификатов...</EmptyText>
        )}

        {!loading && error && (
          <EmptyText>{error}</EmptyText>
        )}

        {!loading && !error && items.length === 0 && (
          <EmptyText>Сертификаты ещё не добавлены.</EmptyText>
        )}

        {!loading && !error && items.length > 0 && (
          <Grid>
            {items.map((item) => {
              const thumbUrl = getImageUrl(item.image_path);
              const title = item.title || 'Сертификат качества';
              return (
                <Card key={item.id} onClick={() => handleOpen(item)} aria-label={title}>
                  <CardImage src={thumbUrl} alt={title} />
                  <CardTitle>{title}</CardTitle>
                </Card>
              );
            })}
          </Grid>
        )}
      </Content>

      {selected && (
        <ModalOverlay onClick={handleClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selected.title || 'Сертификат качества'}</ModalTitle>
              <CloseButton onClick={handleClose} aria-label="Закрыть">×</CloseButton>
            </ModalHeader>
            <ModalImageWrapper>
              <ModalImage src={getImageUrl(selected.image_path)} alt={selected.title || 'Сертификат качества'} />
            </ModalImageWrapper>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default Certificates;

